import axios, {
  AxiosRequestConfig,
  AxiosResponse
} from 'axios';
import {
  AdminSurfaceSession,
  hasAdminSurfaceIdentity,
  MULTI_COMPANY_CONTRACT_VERSION,
  parseAdminSurfaceSession,
  parseMultiCompanyErrorEnvelope,
  parseMultiCompanySuccessEnvelope
} from '../contracts/multiCompany/v1';

export const ADMIN_REALM_AUTH_MODE = 'admin-realm' as const;
export const LEGACY_ADMIN_AUTH_MODE = 'legacy' as const;

export const ADMIN_REALM_LOGIN_PATH = '/auth/admin/login';
export const ADMIN_REALM_REFRESH_PATH = '/auth/admin/refresh';
export const ADMIN_REALM_LOGOUT_PATH = '/auth/admin/logout';
export const LEGACY_ADMIN_LOGIN_PATH = '/auth/login';
export const LEGACY_ADMIN_REFRESH_PATH = '/auth/refresh';
export const LEGACY_ADMIN_LOGOUT_PATH = '/auth/logout';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export class AdminRealmAuthContractError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'AdminRealmAuthContractError';
  }
}

export const parseEnabledAdminRealmSession = (
  value: unknown
): AdminSurfaceSession => {
  const envelope = parseMultiCompanySuccessEnvelope(value);
  if (
    envelope?.contractVersion !== MULTI_COMPANY_CONTRACT_VERSION ||
    !hasAdminSurfaceIdentity(envelope.capabilities)
  ) {
    throw new AdminRealmAuthContractError(
      'Admin realm response is missing its required contract or capability.'
    );
  }

  const session = parseAdminSurfaceSession(envelope.data);
  if (!session) {
    throw new AdminRealmAuthContractError(
      'Admin realm response contains an invalid surface session.'
    );
  }
  return session;
};

export const isTypedAdminRealmFeatureDisabled = (
  error: unknown
): boolean => {
  if (!axios.isAxiosError(error)) return false;

  const parsed = parseMultiCompanyErrorEnvelope(error.response?.data);
  return (
    error.response?.status === 404 &&
    parsed?.statusCode === 404 &&
    parsed.contractVersion === MULTI_COMPANY_CONTRACT_VERSION &&
    parsed.code.known &&
    parsed.code.value === 'MULTI_COMPANY_FEATURE_DISABLED' &&
    parsed.reasonCode?.known === true &&
    parsed.reasonCode.value === 'FEATURE_DISABLED'
  );
};

const withLegacyMode = (response: AxiosResponse): AxiosResponse => {
  if (!isRecord(response.data) || !isRecord(response.data.data)) {
    return response;
  }

  return {
    ...response,
    data: {
      ...response.data,
      data: {
        ...response.data.data,
        authMode: LEGACY_ADMIN_AUTH_MODE
      }
    }
  };
};

const withAdminRealmSession = (
  response: AxiosResponse,
  session: AdminSurfaceSession
): AxiosResponse => ({
  ...response,
  data: {
    ...response.data,
    data: {
      authMode: ADMIN_REALM_AUTH_MODE,
      principal: session.principal,
      user: {
        id: session.principal.id,
        Role: { name: session.principal.role }
      },
      accessToken: session.accessToken,
      refreshToken: session.refreshToken
    }
  }
});

export const loginAdminWithCompatibility = async (
  credentials: unknown,
  config?: AxiosRequestConfig
): Promise<AxiosResponse> => {
  try {
    const realmResponse = await axios.post(
      ADMIN_REALM_LOGIN_PATH,
      credentials,
      config
    );
    return withAdminRealmSession(
      realmResponse,
      parseEnabledAdminRealmSession(realmResponse.data)
    );
  } catch (error) {
    if (!isTypedAdminRealmFeatureDisabled(error)) throw error;
  }

  const legacyResponse = await axios.post(
    LEGACY_ADMIN_LOGIN_PATH,
    credentials,
    config
  );
  return withLegacyMode(legacyResponse);
};
